import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { practiceReviewPath } from '../lib/workItems.js';
import { useLessonPreview } from '../lib/useLessonPreview.js';

export function LessonPlacementBanner({ workItemId, onApprovalReady }: { workItemId: string; onApprovalReady?: (ready: boolean) => void }) {
  const [searchParams] = useSearchParams();
  const state = useLessonPreview(workItemId);
  const ready = state.status === 'ready' && state.preview.kind === 'lesson' && !state.preview.refusal && state.preview.placement.current;
  useEffect(() => { onApprovalReady?.(ready); }, [ready, onApprovalReady]);
  if (state.status === 'loading') return <p className="mt-2 text-sm text-slate-500">Loading lesson placement…</p>;
  if (state.status === 'error' || state.preview.kind !== 'lesson') {
    return <div className="mt-2 text-sm text-red-800" role="alert"><p>Could not load lesson placement.</p><button type="button" className="mt-2 font-bold underline" onClick={state.retry}>Retry</button></div>;
  }
  const { placement, prerequisites, questions, refusal } = state.preview;
  return (
    <section className="mt-2 max-h-60 space-y-2 overflow-auto rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm" data-testid="lesson-placement">
      <p className="font-semibold text-blue-950">
        Placed in {placement.groupTitle ?? `the lesson group (title unavailable; reference ${placement.groupId})`}
        {placement.after ? `, after ${placement.after.title ?? 'Untitled lesson'} (#${placement.after.id})` : ''}
        {placement.before ? `, before ${placement.before.title ?? 'Untitled lesson'} (#${placement.before.id})` : ', at the end'}
      </p>
      {!placement.current || refusal ? (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-red-900" role="alert">
          <p>The release would refuse this lesson as it stands; reject it so it can be redrafted against the course as it is now.</p>
          {!placement.current ? <p>The lesson placement has changed since this lesson was drafted; it needs a redraft.</p> : null}
          {refusal ? <p>{refusal.message}</p> : null}
        </div>
      ) : null}
      <h3 className="font-bold">Prerequisites</h3>
      {prerequisites.length ? <ul>{prerequisites.map((prerequisite) => (
        <li key={prerequisite.id}>
          {prerequisite.title ?? 'Untitled lesson'} (#{prerequisite.id})
          {!prerequisite.live || !prerequisite.published || prerequisite.served === false ? (
            <span className="ml-2 font-semibold text-amber-900">⚠ {[
              !prerequisite.live ? 'Not live' : '',
              !prerequisite.published ? 'Not published' : '',
              prerequisite.live && prerequisite.published && prerequisite.served === false ? 'Not shown to learners' : '',
            ].filter(Boolean).join(' · ')}</span>
          ) : null}
        </li>
      ))}</ul> : <p>No prerequisites.</p>}
      <h3 className="font-bold">Practice questions</h3>
      {questions.length ? <ul>{questions.map((question) => (
        <li key={question.workItemId}>
          <Link className="text-blue-700 underline" to={practiceReviewPath(question.workItemId, searchParams)}>{question.title || `Practice question (review reference ${question.workItemId})`}</Link>
          <span className="ml-2 text-slate-600">{question.status.replaceAll('_', ' ')}</span>
        </li>
      ))}</ul> : <p>No practice questions.</p>}
    </section>
  );
}
